/*
<summary>
   This class component is all about Managing the User Created Inspection (Equipment
   Inspection) session — fetching a tool's latest inspection record
   (ToolManagement/GetUserCreatedInspectionByToolId) and submitting Start/Pause/Complete
   state transitions (ToolManagement/AddUserCreatedInspection).
   Developer: Aashish Singh, Created Date: 24-Aug-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns class instance</returns>
*/

import { action, makeObservable, observable } from 'mobx';
import toast from 'react-hot-toast';
import { errorMessage } from '../../constants/error-constants';
import URLConstants from '../../constants/url-constants';
import { IAddUserCreatedInspectionRequest } from '../../models/forms/IAddUserCreatedInspection';
import IApiResponse, { IApiSuccessResponse } from '../../models/response/IApiResponse';
import { IToolCheckInfoMaster } from '../../models/response/IUserInspectedListResponse';
import { formatMessage } from '../../translations/format-message';
import { ObservableInitialState } from '../initialState';
import * as baseService from '../service/base-service';

export class EquipmentInspectionStore {
  initialStateValue = ObservableInitialState;
  userCreatedInspectionDetail: IToolCheckInfoMaster | null = null;
  getUserCreatedInspectionState = { ...this.initialStateValue };
  addUserCreatedInspectionState = { ...this.initialStateValue };

  constructor() {
    makeObservable(this, {
      userCreatedInspectionDetail: observable,
      getUserCreatedInspectionState: observable,
      addUserCreatedInspectionState: observable,
      GetUserCreatedInspectionByToolIdService: action,
      AddUserCreatedInspectionService: action,
      resetAddUserCreatedInspectionState: action,
      reset: action,
      resetStore: action,
    });
  }

  /**
  * This function is used to get a tool's latest User Created Inspection session (if any)
  * from the real backend API (ToolManagement/GetUserCreatedInspectionByToolId). A "not
  * found" response is expected/normal for a tool that has never been inspected yet, so it
  * is treated as "no session" rather than surfaced as an error toast.
  * @param toolId: Tool Id to look up
  */
  GetUserCreatedInspectionByToolIdService = (toolId: number) => {
    this.getUserCreatedInspectionState.inProgress = true;
    const url = `${URLConstants.GetUserCreatedInspectionByToolId}?toolId=${toolId}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IToolCheckInfoMaster>>) => {
        if (response.data.Error) {
          // "Inspection record not found" is the normal case for a tool being inspected
          // for the first time - not an error condition to surface to the user.
          this.userCreatedInspectionDetail = null;
        } else {
          this.userCreatedInspectionDetail = response.data.Data;
        }
      })
      .catch(() => {
        this.userCreatedInspectionDetail = null;
      })
      .finally(
        action(() => {
          this.getUserCreatedInspectionState.inProgress = false;
        })
      );
  };

  /**
  * This function is used to submit a Start/Pause/Complete state transition for a User
  * Created Inspection session by API
  * (ToolManagement/AddUserCreatedInspection).
  * @param payload: Inspection session payload (State: 1 = Start, 2 = Pause, 3 = Completed)
  */
  AddUserCreatedInspectionService = (payload: IAddUserCreatedInspectionRequest) => {
    this.addUserCreatedInspectionState.inProgress = true;
    return baseService
      .postRequest(URLConstants.AddUserCreatedInspection, payload)
      .then((response: IApiResponse<IApiSuccessResponse<any>>) => {
        if (response.data.Error) {
          this.addUserCreatedInspectionState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
          return false;
        }
        this.addUserCreatedInspectionState.success = true;
        return true;
      })
      .catch(() => {
        this.addUserCreatedInspectionState.error = errorMessage.SomethingWentWrong;
        toast.error(formatMessage(errorMessage.SomethingWentWrong));
        return false;
      })
      .finally(
        action(() => {
          this.addUserCreatedInspectionState.inProgress = false;
        })
      );
  };

  /**
  * This function is used to reset addUserCreatedInspectionState observables to their
  * initial values.
  */
  resetAddUserCreatedInspectionState = () => {
    this.addUserCreatedInspectionState = { ...this.initialStateValue };
  };

  /**
  * This function is used to reset the store's observables to their initial values.
  */
  reset = () => {
    this.userCreatedInspectionDetail = null;
    this.getUserCreatedInspectionState = { ...this.initialStateValue };
    this.addUserCreatedInspectionState = { ...this.initialStateValue };
  };

  /**
  * This function is used to reset all store observables to their initial values.
  */
  resetStore = () => {
    this.userCreatedInspectionDetail = null;
    this.getUserCreatedInspectionState = { ...this.initialStateValue };
    this.addUserCreatedInspectionState = { ...this.initialStateValue };
  };
}

export default new EquipmentInspectionStore();
